/**
 * Running a spec.
 *
 * The library does not know how to reach your server, and deliberately so —
 * Clockwise talks to Hono behind session auth, another host may use anything.
 * A `Transport` is injected; `report-kit` never imports fetch config, auth or
 * a query client.
 *
 * This hook is intentionally small (no cache, no retry): a host using TanStack
 * Query should pass its own transport and ignore this hook entirely. It exists
 * so the builder is usable with nothing else installed.
 */
import type { QueryPlan, QueryResult, ReportSpec } from '@clockwise/semantic-core';
import { planKey } from '@clockwise/semantic-core';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface Transport {
  runQuery(spec: ReportSpec, signal?: AbortSignal): Promise<QueryResult>;
}

export interface QueryState {
  data: QueryResult | null;
  loading: boolean;
  error: Error | null;
  /** Re-run the current spec. */
  refetch: () => void;
}

/**
 * Run a spec, re-running when the QUERY changes — or when `dependsOn` does.
 *
 * The plan decides the first: `planKey` covers what the server answers and
 * nothing else, so toggling a chart type, a total, a share or a client-side
 * sort does not re-run an aggregate whose rows would come back identical.
 *
 * `dependsOn` is the escape hatch for a question the PLAN cannot see: a host
 * that asks its transport for something extra — the compiled SQL, an
 * `explain` — changes what the server sends back without changing the plan
 * at all, and this hook has no way to notice that on its own. `transport`
 * itself cannot be the signal: a host that (reasonably) builds a fresh
 * transport object every render would refetch on every render, forever. So
 * the host names the one thing that actually varies — `includeSql`, say —
 * and passing a new value here is what asks again. Compared with `===`;
 * strings and booleans are the expected shapes, an object recreated every
 * render is not.
 */
export function useReportQuery(
  spec: ReportSpec | null,
  plan: QueryPlan | null,
  transport: Transport,
  dependsOn?: string | number | boolean,
): QueryState {
  // The result is stored WITH the key it answered, so a result can never be
  // rendered against a spec it did not come from — see `data` below.
  const [entry, setEntry] = useState<{ key: string; data: QueryResult } | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);

  // Folded into the SAME key `planKey` produces, rather than tracked
  // separately, so every place that already compares keys — the effect's
  // guard, `data` below — picks up the new dimension for free.
  const key = spec && plan ? `${planKey(plan)}::${dependsOn ?? ''}` : null;

  const transportRef = useRef(transport);
  transportRef.current = transport;
  // The FULL spec still goes to the transport; only the key is narrowed.
  const latest = useRef<{ key: string; spec: ReportSpec } | null>(null);
  latest.current = key && spec ? { key, spec } : null;

  // `nonce` is not read inside the effect — it IS the refetch trigger. Dropping
  // it, as the rule suggests, would make refetch() a no-op for an unchanged spec.
  // biome-ignore lint/correctness/useExhaustiveDependencies: nonce is the refetch trigger
  useEffect(() => {
    const current = latest.current;
    // The ref is written during render and read after commit. If another render
    // has landed in between, its own effect will fire with the right pairing —
    // issuing this one would file a result under a key it did not answer.
    if (!key || current?.key !== key) {
      if (!key) {
        setError(null);
        setLoading(false);
      }
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(null);

    const fail = (err: unknown) => {
      if (cancelled || controller.signal.aborted) return;
      setError(err instanceof Error ? err : new Error(String(err)));
      setLoading(false);
    };

    // `Transport` is the host's extension point, so a transport that throws
    // BEFORE returning a promise — an unconfigured client, a bad URL built
    // eagerly — is a mistake this hook should report rather than let escape
    // the effect and take the component down with it.
    try {
      transportRef.current
        .runQuery(current.spec, controller.signal)
        .then((result) => {
          if (cancelled) return;
          setEntry({ key, data: result });
          setLoading(false);
        })
        .catch(fail);
    } catch (err) {
      fail(err);
    }

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [key, nonce]);

  // A result belongs to exactly one query. If the spec has moved on — because
  // the request failed, or simply because this render is the one before the
  // effect fires — there is no data for the current spec, and saying so beats
  // projecting the old rows onto the new axes and inventing empty columns.
  const data = entry && entry.key === key ? entry.data : null;

  const refetch = useCallback(() => setNonce((n) => n + 1), []);
  return { data, loading, error, refetch };
}

/**
 * Transport over a JSON endpoint.
 *
 * Provided as a convenience for the common case; a host with its own client
 * (typed SDK, auth headers, tracing) should implement `Transport` directly.
 */
export function httpTransport(url: string, init?: RequestInit): Transport {
  return {
    async runQuery(spec, signal) {
      // `init` FIRST: it contributes credentials, mode and the like, but must
      // not replace the method, the body or the abort signal. Headers are
      // merged through `Headers` rather than spread — `HeadersInit` is also a
      // Headers instance or an array of pairs, and spreading either silently
      // discards every header the caller set.
      const headers = new Headers(init?.headers);
      headers.set('content-type', 'application/json');

      const res = await fetch(url, {
        ...init,
        method: 'POST',
        headers,
        body: JSON.stringify(spec),
        signal,
      });
      if (!res.ok) {
        // Surface the server's own message — a refusal is written for humans
        // and is the most useful thing in the response.
        const body = (await res.json().catch(() => null)) as {
          error?: { message?: string };
        } | null;
        throw new Error(body?.error?.message ?? `Query failed (${res.status})`);
      }
      return (await res.json()) as QueryResult;
    },
  };
}
